import { ausschreibungStatus, type ExperteAntwort, experteAntworten, type Leistung, vivaStatus } from '../../../dtos';
import { isTerminvorschlaegePruefenEnabled } from './terminvorschlaegePruefenFeature.ts';

export type AntwortCell =
  | { readonly art: 'angenommen' }
  | { readonly art: 'abgelehnt' }
  | { readonly art: 'abgelaufenAusschreibung' }
  | { readonly art: 'abgelaufenAngebot' }
  | { readonly art: 'vorbehalt'; readonly vorschlaege: ExperteAntwort['alternativvorschlaege'] }
  | { readonly art: 'keineAntwort' };

export function hasVorschlaegeForTeilleistung(
  experteAntwortenList: readonly ExperteAntwort[],
  teilleistungId: string,
): boolean {
  if (hasAusschreibungszusage(experteAntwortenList)) {
    return false;
  }

  return experteAntwortenList.some(
    (experteAntwort) =>
      experteAntwort.antwort === experteAntworten.AUSSCHREIBUNG_UNTER_VORBEHALT_ANGENOMMEN &&
      experteAntwort.alternativvorschlaege.some(
        (alternativvorschlag) =>
          alternativvorschlag.teilleistungId === teilleistungId && alternativvorschlag.leistungszeitraeume.length > 0,
      ),
  );
}

function getTeilleistungIdsMitAlternativvorschlaegen(experteAntwortenList: readonly ExperteAntwort[]): string[] {
  const teilleistungIds = new Set<string>();

  for (const experteAntwort of experteAntwortenList) {
    if (experteAntwort.antwort !== experteAntworten.AUSSCHREIBUNG_UNTER_VORBEHALT_ANGENOMMEN) {
      continue;
    }
    for (const alternativvorschlag of experteAntwort.alternativvorschlaege.filter(
      (av) => av.leistungszeitraeume.length > 0,
    )) {
      teilleistungIds.add(alternativvorschlag.teilleistungId);
    }
  }

  return [...teilleistungIds];
}

export function isTeilleistungGeprueft(
  experteAntwortenList: readonly ExperteAntwort[],
  teilleistungId: string,
): boolean {
  const hasSelectedAlternativvorschlag = experteAntwortenList.some((experteAntwort) =>
    experteAntwort.alternativvorschlaege.some(
      (alternativvorschlag) => alternativvorschlag.teilleistungId === teilleistungId && alternativvorschlag.isSelected,
    ),
  );
  if (hasSelectedAlternativvorschlag) {
    return true;
  }

  return experteAntwortenList.some(
    (experteAntwort) => getAntwortCell(experteAntwort, teilleistungId).art === 'angenommen',
  );
}

export function hasPruefenOderAendernForTeilleistung(
  experteAntwortenList: readonly ExperteAntwort[],
  teilleistungId: string,
): boolean {
  return (
    hasVorschlaegeForTeilleistung(experteAntwortenList, teilleistungId) ||
    experteAntwortenList.some((experteAntwort) =>
      experteAntwort.alternativvorschlaege.some(
        (alternativvorschlag) =>
          alternativvorschlag.teilleistungId === teilleistungId && alternativvorschlag.isSelected,
      ),
    )
  );
}

export function isAusschreibungAbgebrochen(leistung: Leistung): boolean {
  return leistung.ausschreibung?.status === ausschreibungStatus.AUSSCHREIBUNG_ABGEBROCHEN;
}

export function isPruefenFlowAktiv(leistung: Leistung): boolean {
  if (!isTerminvorschlaegePruefenEnabled()) {
    return false;
  }

  if (isAusschreibungAbgebrochen(leistung)) {
    return false;
  }

  if (leistung.statusTransitions.includes(vivaStatus.IM_AUSSCHREIBUNGSPORTAL)) {
    return false;
  }

  const experteAntworten = leistung.ausschreibung?.experteAntworten ?? [];
  if (hasAusschreibungszusage(experteAntworten)) {
    return false;
  }

  return getTeilleistungIdsMitAlternativvorschlaegen(experteAntworten).some(
    (teilleistungId) =>
      hasVorschlaegeForTeilleistung(experteAntworten, teilleistungId) &&
      !isTeilleistungGeprueft(experteAntworten, teilleistungId),
  );
}

function hasAusschreibungszusage(experteAntwortenList: readonly ExperteAntwort[]): boolean {
  return experteAntwortenList.some(
    (experteAntwort) => experteAntwort.antwort === experteAntworten.AUSSCHREIBUNG_ANGENOMMEN,
  );
}

export function getAntwortCell(experteAntwort: ExperteAntwort, teilleistungId: string): AntwortCell {
  switch (experteAntwort.antwort) {
    case experteAntworten.AUSSCHREIBUNG_ANGENOMMEN:
    case experteAntworten.ANGEBOT_ANGENOMMEN:
      return { art: 'angenommen' };
    case experteAntworten.AUSSCHREIBUNG_ABGELEHNT:
    case experteAntworten.ANGEBOT_ABGELEHNT:
      return { art: 'abgelehnt' };
    case experteAntworten.AUSSCHREIBUNG_ABGELAUFEN:
      return { art: 'abgelaufenAusschreibung' };
    case experteAntworten.ANGEBOT_ABGELAUFEN:
      return { art: 'abgelaufenAngebot' };
    case experteAntworten.AUSSCHREIBUNG_UNTER_VORBEHALT_ANGENOMMEN: {
      const vorschlaege = experteAntwort.alternativvorschlaege.filter(
        (alternativvorschlag) => alternativvorschlag.teilleistungId === teilleistungId,
      );
      if (vorschlaege.length > 0) {
        return { art: 'vorbehalt', vorschlaege };
      }
      return { art: 'angenommen' };
    }
    default:
      return { art: 'keineAntwort' };
  }
}
